import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { dateFormat, dateFormatWithoutHours, REQUEST_STATUS } from '@shared/constants/otherConstants';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import { getTestPlansRequest, getTestPlansReset } from '@pages/testplans/store';
import {
    selectDeleteTestPlanStatus,
    selectDuplicateTestPlanStatus,
    selectGetTestPlansErrorInfo,
    selectGetTestPlansStatus,
    selectTestPlans,
    selectTestPlansCount,
} from '@pages/testplans/store/selectors';
import { Link, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePrevious } from '@shared/hooks/usePrevious';
import TestRunsPopover from './TestRunsPopover';
import { TESTPLAN_STATUS, TESTPLAN_STATUS_COLOR } from '@pages/testplans/constants/testplansСonstants';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
    UI_FUNCTIONAL_TESTING_PATH,
    UI_TEST_CASE_PATH,
    UI_TEST_PLAN_PATH,
    UI_TEST_RUN_PATH,
} from '@shared/constants/pathsConstants';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import CustomTable from '@shared/components/others/CustomTable/CustomTable';
import { useListState } from '@shared/hooks/useListState';
import UtmsChip from '@shared/components/ui/UtmsChip';
import { useTestplansChunk } from '@pages/testplans/hooks/useTestplansChunk';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { TestCasesPopover } from '@pages/testplans';
import { toTitleCase } from '@shared/utils/utilsOld';
import { useCreatedByUserColumn } from '@shared/hooks/useCreatedByUserColumn';
import TestingEndDateCell from './TestingEndDateCell';
import UtmsPagination from '@shared/components/ui/UtmsPagination';

const testPlanStatuses = Object.values(TESTPLAN_STATUS);

export const TestplansTable = (props) => (
    <ComponentWithChunks
        featuresArray={['testPlans']}
        hooksArray={[useTestplansChunk]}
        Component={TestplansTableComponent}
        {...props}
    />
);

const TestplansTableComponent = ({ toolbar, dialogMode, setSelectedTestplanId }) => {
    const {
        rowSelection,
        setRowSelection,
        pagination,
        setPagination,
        globalFilter,
        setGlobalFilter,
        showColumnFilters,
        setShowColumnFilters,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        fetchData,
    } = useListState(!dialogMode ? 'testPlansTable' : '', false, getTestPlansRequest);

    const getTestPlansStatus = useSelector(selectGetTestPlansStatus);
    const testPlans = useSelector(selectTestPlans);
    const { t } = useTranslation();
    const [testcasesPopoverAnchorEl, setTestcasesPopoverAnchorEl] = useState(null);
    const [testRunsPopoverAnchorEl, setTestRunsPopoverAnchorEl] = useState(null);
    const [testplanIdForPopover, setTestPlanIdForPopover] = useState(null);
    const deleteTestPlanStatus = useSelector(selectDeleteTestPlanStatus);
    const prevDeleteTestplanStatus = usePrevious(deleteTestPlanStatus);
    const navigate = useNavigate();
    const { pageIndex } = pagination;
    const testPlansCount = useSelector(selectTestPlansCount);
    const duplicateTestPlanStatus = useSelector(selectDuplicateTestPlanStatus);
    const prevDuplicateTestPlanStatus = usePrevious(duplicateTestPlanStatus);
    const createdByUserColumn = useCreatedByUserColumn({ columnFilters, setColumnFilters });

    useEffect(() => {
        if (setSelectedTestplanId) {
            for (let key in rowSelection) {
                if (rowSelection[key] === undefined) {
                    continue;
                }

                setSelectedTestplanId(rowSelection[key] ? key : null);
            }
        }
    }, [rowSelection, setSelectedTestplanId]);

    useEffect(() => {
        if (!testRunsPopoverAnchorEl && !testcasesPopoverAnchorEl) {
            setTestPlanIdForPopover(null);
        }
    }, [testRunsPopoverAnchorEl, testcasesPopoverAnchorEl]);

    const resetSelection = useCallback(() => {
        setRowSelection({});
        setSelectedTestplanId && setSelectedTestplanId(null);
    }, [setRowSelection, setSelectedTestplanId]);

    useEffect(() => {
        if (deleteTestPlanStatus === REQUEST_STATUS.SUCCESS && prevDeleteTestplanStatus !== deleteTestPlanStatus) {
            resetSelection();
            fetchData();
        }
        if (
            duplicateTestPlanStatus === REQUEST_STATUS.SUCCESS &&
            prevDuplicateTestPlanStatus !== duplicateTestPlanStatus
        ) {
            resetSelection();
            pageIndex > 0 ? setPagination((prev) => ({ ...prev, pageIndex: 0 })) : fetchData('duplicate');
        }
    }, [
        deleteTestPlanStatus,
        duplicateTestPlanStatus,
        fetchData,
        pageIndex,
        prevDeleteTestplanStatus,
        prevDuplicateTestPlanStatus,
        resetSelection,
        setPagination,
    ]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'pk',
                header: 'ID',
                maxSize: 104,
            },
            {
                accessorKey: 'title',
                header: t('test_plan'),
                Cell: ({ cell }) => {
                    const testPlan = cell.row.original;
                    return (
                        <OverflowTooltip title={testPlan.title}>
                            <Link
                                underline="hover"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_PLAN_PATH}/${testPlan.pk}`);
                                }}
                                sx={{ cursor: 'pointer' }}
                                href={`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_PLAN_PATH}/${testPlan.pk}`}
                            >
                                {testPlan.title}
                            </Link>
                        </OverflowTooltip>
                    );
                },
            },
            {
                accessorKey: 'start_date',
                header: toTitleCase(t('start_date')),
                filterVariant: 'date',
                Cell: ({ cell }) => {
                    const date = cell.row.original['start_date'];
                    return date ? dayjs(date).format(dateFormatWithoutHours) : t('no_info');
                },
            },
            {
                accessorKey: 'status',
                header: t('status'),
                maxSize: 140,
                Cell: ({ cell }) => {
                    const value = cell.row.original?.status;
                    return (
                        value && (
                            <UtmsChip
                                label={t(value.toLowerCase())}
                                color={TESTPLAN_STATUS_COLOR[value.toUpperCase()]}
                            />
                        )
                    );
                },
                filterVariant: 'select',
                filterSelectOptions: testPlanStatuses.map((item) => ({
                    label: <UtmsChip label={t(item.toLowerCase())} color={TESTPLAN_STATUS_COLOR[item.toUpperCase()]} />,
                    value: item,
                })),
            },
            {
                accessorKey: 'end_date',
                header: toTitleCase(t('due_date')),
                filterVariant: 'date',
                Cell: ({ cell }) => <TestingEndDateCell date={cell.row.original.end_date} />,
            },
            {
                accessorKey: 'description',
                header: t('description'),
                maxSize: 200,

                Cell: ({ cell }) => {
                    const description = cell.row.original?.description;

                    if (!description) {
                        return '';
                    }

                    return description.length < 80 ? (
                        description
                    ) : (
                        <Tooltip title={description}>
                            <span>{description.slice(0, description[79] === ' ' ? 79 : 80) + '...'}</span>
                        </Tooltip>
                    );
                },
            },
            ...(!dialogMode
                ? [
                      {
                          accessorKey: 'testcases',
                          header: t('content'),
                          Cell: ({ cell }) => {
                              const testcasesCount = cell.row.original['testcases_cnt'];
                              const testCase = cell.row.original['testcase'];
                              const testplanId = cell.row.original.pk;
                              if (testCase) {
                                  return (
                                      <OverflowTooltip title={testCase.name}>
                                          <Link
                                              underline="hover"
                                              onClick={(e) => {
                                                  e.preventDefault();
                                                  navigate(
                                                      `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${testCase.pk}`,
                                                  );
                                              }}
                                              href={`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${testCase.pk}`}
                                              sx={{ cursor: 'pointer' }}
                                          >
                                              {testCase.name}
                                          </Link>
                                      </OverflowTooltip>
                                  );
                              }
                              const countLabel = t('testcaseWithCount', { count: testcasesCount });

                              return testcasesCount > 0 ? (
                                  <Link
                                      underline="hover"
                                      onClick={handleOpenTestcasesPopover(testplanId)}
                                      sx={{ cursor: 'pointer' }}
                                  >
                                      {countLabel}
                                  </Link>
                              ) : (
                                  countLabel
                              );
                          },
                          enableColumnFilter: false,
                      },
                      {
                          accessorKey: 'test_runs',
                          header: t('test_runs'),
                          Cell: ({ cell }) => {
                              const testRunsCount = cell.row.original['testruns_cnt'];
                              const testplanId = cell.row.original.pk;
                              const testrun = cell.row.original.testrun;
                              const date = testrun?.createdAt && dayjs(testrun.createdAt).format(dateFormat);
                              if (date) {
                                  return (
                                      <Link
                                          underline="hover"
                                          onClick={(e) => {
                                              e.preventDefault();
                                              navigate(
                                                  `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_RUN_PATH}/${testrun.pk}`,
                                              );
                                          }}
                                          href={`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_RUN_PATH}/${testrun.pk}`}
                                          sx={{ cursor: 'pointer' }}
                                      >
                                          {date}
                                      </Link>
                                  );
                              }
                              return testRunsCount === 0 ? (
                                  '-'
                              ) : (
                                  <Link
                                      underline="hover"
                                      onClick={handleOpenTestrunsPopover(testplanId)}
                                      sx={{ cursor: 'pointer' }}
                                  >
                                      {t('testrunWithCount', { count: testRunsCount })}
                                  </Link>
                              );
                          },
                          enableColumnFilter: false,
                      },
                  ]
                : []),
            {
                accessorKey: 'last_testrun',
                header: t('last_run'),
                Cell: ({ cell }) => {
                    const lastTestRun = cell.row.original['last_testrun'];
                    const date = lastTestRun?.createdAt;
                    return date ? (
                        <Link
                            underline="hover"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_RUN_PATH}/${lastTestRun.pk}`);
                            }}
                            href={`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_RUN_PATH}/${lastTestRun.pk}`}
                            sx={{ cursor: 'pointer' }}
                        >
                            {dayjs(date).format(dateFormat)}
                        </Link>
                    ) : (
                        '-'
                    );
                },
                filterVariant: 'date',
            },
            {
                accessorKey: 'createdAt',
                header: toTitleCase(t('created')),
                filterVariant: 'date',
                Cell: ({ cell }) => {
                    const date = cell.row.original['createdAt'];
                    return date ? dayjs(date).format(dateFormat) : t('no_info');
                },
            },
            createdByUserColumn,
        ],
        [dialogMode, navigate, t, createdByUserColumn],
    );

    const handleOpenTestcasesPopover = (testplanId) => (event) => {
        event.stopPropagation();
        setTestPlanIdForPopover(testplanId);
        setTestcasesPopoverAnchorEl(event.currentTarget);
    };

    const handleOpenTestrunsPopover = (testplanId) => (event) => {
        event.stopPropagation();
        setTestPlanIdForPopover(testplanId);
        setTestRunsPopoverAnchorEl(event.currentTarget);
    };

    return (
        <>
            {getTestPlansStatus === REQUEST_STATUS.ERROR && (
                <ModalErrorContainer
                    object={t('test_plans_r')}
                    action={t('fetching')}
                    resetActionCallback={getTestPlansReset}
                    errorSelector={selectGetTestPlansErrorInfo}
                />
            )}
            <CustomTable
                tableId="testplans-table"
                columns={columns}
                data={testPlans}
                muiSearchTextFieldProps={{
                    variant: 'outlined',
                    placeholder: `${t('test_plan')}...`,
                    InputProps: {
                        sx: {
                            pl: 1.5,
                            pr: 1.5,
                        },
                    },
                    inputProps: {
                        sx: {
                            marginLeft: 0.5,
                        },
                    },
                }}
                toolbar={toolbar}
                dialogMode={dialogMode}
                state={{
                    showSkeletons: getTestPlansStatus === REQUEST_STATUS.LOADING,
                    showColumnFilters,
                    rowSelection,
                    globalFilter,
                    pagination,
                    sorting,
                    columnFilters,
                }}
                muiTableBodyRowProps={({ row }) => ({
                    onClick: () =>
                        getTestPlansStatus === REQUEST_STATUS.SUCCESS
                            ? setRowSelection((prev) => ({
                                  [row.id]: !prev[row.id],
                              }))
                            : undefined,
                    onDoubleClick: (e) => {
                        e.preventDefault();
                        navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_PLAN_PATH}/${row.original.pk}`);
                    },
                    selected: rowSelection[row.id],
                })}
                onRowSelectionChange={setRowSelection}
                onShowColumnFiltersChange={setShowColumnFilters}
                onGlobalFilterChange={setGlobalFilter}
                onPaginationChange={setPagination}
                rowCount={testPlansCount}
                enablePagination={false}
                muiBottomToolbarProps={{
                    sx: {
                        minHeight: 0,
                        boxShadow: 0,
                        backgroundColor: 'inherit',
                    },
                }}
                renderBottomToolbar={() => (
                    <UtmsPagination
                        view="table"
                        pagination={pagination}
                        count={testPlansCount}
                        handleChangePagination={setPagination}
                    />
                )}
                onSortingChange={setSorting}
                onColumnFiltersChange={setColumnFilters}
            />
            {testplanIdForPopover && (
                <TestCasesPopover
                    testcasesPopoverAnchorEl={testcasesPopoverAnchorEl}
                    setTestcasesPopoverAnchorEl={setTestcasesPopoverAnchorEl}
                    testplanId={testplanIdForPopover}
                />
            )}
            <TestRunsPopover
                testrunsPopoverAnchorEl={testRunsPopoverAnchorEl}
                setTestrunsPopoverAnchorEl={setTestRunsPopoverAnchorEl}
                testplanId={testplanIdForPopover}
            />
        </>
    );
};
